export function latexToSpeech(input: string): string {
    if (!input) return '';

    let s = input;
    s = s.replace(/\$\\\\\s*\\,\s*\\\\\$/g, ', ');
    s = s.replace(/\$\\\\\$/g, ', ');

    const parts = s.split(/(\$\$[\s\S]*?\$\$|\$(?:\\\$|[^$])+\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\))/g);
    const out = parts.map(part => {
        if (part.length >= 4 && part.startsWith('$$') && part.endsWith('$$')) {
            return ' ' + transformMath(part.slice(2, -2)) + ' ';
        }
        if (part.length >= 2 && part.startsWith('$') && part.endsWith('$')) {
            return ' ' + transformMath(part.slice(1, -1)) + ' ';
        }
        if (part.length >= 4 && part.startsWith('\\[') && part.endsWith('\\]')) {
            return ' ' + transformMath(part.slice(2, -2)) + ' ';
        }
        if (part.length >= 4 && part.startsWith('\\(') && part.endsWith('\\)')) {
            return ' ' + transformMath(part.slice(2, -2)) + ' ';
        }
        return part;
    });

    let result = out.join('');

    result = result.replace(/\\%/g, ' percent ');
    result = result.replace(/\\\$/g, '$');

    result = result.replace(/(\d)\s*,\s*(?=\d{3}\b)/g, '$1');

    result = result.replace(/([A-Za-z])\/([A-Za-z])/g, '$1 per $2');

    result = result.replace(/(^|[(=+,:|])\s*-\s*(?=[\d.a-zA-Z(])/g, '$1 negative ');

    result = result.replace(/([A-Za-z])(\s*)-([a-zA-Z]{3,})/g, '$1$2 $3');

    return result.replace(/\s+/g, ' ').trim();
}

function transformMath(m: string): string {
    const PH = '';

    m = m.replace(/\\(?:footnotesize|small|large|Large|huge|Huge|normalsize|tiny|scriptsize|displaystyle|textstyle|mathrm|mathbf|mathit|mathsf|mathtt|left|right)\b\s*/g, '');

    m = m.replace(/\\(?:hspace|vspace|phantom|hphantom|vphantom|kern|mskip|mspace)\s*\{[^{}]*\}/g, ' ');
    m = m.replace(/\\(?:qquad|quad)\b/g, ' ');

    m = m.replace(/\\begin\s*\{[^{}]*\}(?:\s*\{[^{}]*\})?/g, ' ').replace(/\\end\s*\{[^{}]*\}/g, ' ');

    const textChunks: string[] = [];
    m = m.replace(/\\text(?:it|bf|tt|sf|rm|sc|md|up|sl|normal)?\s*\{([^{}]*)\}/g, (_m, inner) => {
        const idx = textChunks.length;
        textChunks.push(inner);
        return `${PH}T${idx}T${PH}`;
    });

    const cmds: string[] = [];
    let s = m.replace(/\\[a-zA-Z]+/g, match => {
        const idx = cmds.length;
        cmds.push(match);
        return `${PH}${idx}${PH}`;
    });

    s = s.replace(/[A-Za-z]{2,}/g, run => {
        const isAllUpper = /^[A-Z]+$/.test(run);
        if (isAllUpper || run.length <= 2) {
            return run.split('').join(' ');
        }
        return run;
    });

    s = s.replace(new RegExp(`${PH}(\\d+)${PH}`, 'g'), (_m, idx) => cmds[Number(idx)]);

    s = s.replace(/\^\s*(?:\{\s*\\circ\s*\}|\\circ)/g, ' degrees ');
    s = s.replace(/\\circ\b/g, ' degrees ');

    s = s.replace(/\\(?:leq|le)\b/g, ' less than or equal to ');
    s = s.replace(/\\(?:geq|ge)\b/g, ' greater than or equal to ');
    s = s.replace(/\\(?:neq|ne)\b/g, ' not equal to ');
    s = s.replace(/>=/g, ' greater than or equal to ');
    s = s.replace(/<=/g, ' less than or equal to ');
    s = s.replace(/!=/g, ' not equal to ');
    s = s.replace(/>/g, ' greater than ');
    s = s.replace(/</g, ' less than ');
    s = s.replace(/\\approx\b/g, ' approximately ');
    s = s.replace(/\\equiv\b/g, ' equivalent to ');
    s = s.replace(/\\sim\b/g, ' similar to ');
    s = s.replace(/\\propto\b/g, ' proportional to ');
    s = s.replace(/\\cdot\b/g, ' times ');
    s = s.replace(/\\times\b/g, ' times ');
    s = s.replace(/\\div\b/g, ' divided by ');
    s = s.replace(/\\pm\b/g, ' plus or minus ');
    s = s.replace(/\\mp\b/g, ' minus or plus ');
    s = s.replace(/\\(?:to|rightarrow|longrightarrow|Rightarrow|Longrightarrow)\b/g, ' goes to ');
    s = s.replace(/\\(?:leftarrow|longleftarrow|Leftarrow|Longleftarrow)\b/g, ' from ');
    s = s.replace(/\\infty\b/g, ' infinity ');

    s = s.replace(/\\angle\b/g, ' angle ');
    s = s.replace(/\\triangle\b/g, ' triangle ');
    s = s.replace(/\\overline\s*\{([^{}]+)\}/g, (_m, a) => ` segment ${a.trim()} `);
    s = s.replace(/\\underline\s*\{([^{}]+)\}/g, (_m, a) => ` ${a.trim()} `);
    s = s.replace(/\\overgroup\s*\{([^{}]+)\}/g, (_m, a) => ` arc ${a.trim()} `);
    s = s.replace(/\\overrightarrow\s*\{([^{}]+)\}/g, (_m, a) => ` vector ${a.trim()} `);
    s = s.replace(/\\hat\s*\{([^{}]+)\}/g, (_m, a) => ` ${a.trim()} hat `);
    s = s.replace(/\\bar\s*\{([^{}]+)\}/g, (_m, a) => ` ${a.trim()} bar `);

    s = s.replace(/\\(?:big|Big|bigg|Bigg)\|\s*([^|]+?)\s*\\(?:big|Big|bigg|Bigg)\|/g, (_m, a) => {
        let t = a.trim();
        if (t.startsWith('-')) t = 'negative ' + t.slice(1).trim();
        return ` absolute value of ${t} `;
    });

    s = s.replace(/\\textemdash\b/g, ' em dash ');
    s = s.replace(/\\textendash\b/g, ' en dash ');
    s = s.replace(/\\lq\b/g, "'");
    s = s.replace(/\\rq\b/g, "'");
    s = s.replace(/\\ldots\b|\\dots\b|\\cdots\b/g, ' and so on ');

    s = s.replace(/\\textit\s*\{([^{}]+)\}/g, ' $1 ');
    s = s.replace(/\\textbf\s*\{([^{}]+)\}/g, ' $1 ');
    s = s.replace(/\\underbar\s*\{([^{}]+)\}/g, ' $1 ');

    const greek: Array<[string, string]> = [
        ['varepsilon', 'epsilon'], ['epsilon', 'epsilon'],
        ['vartheta', 'theta'], ['theta', 'theta'],
        ['varpi', 'pi'], ['pi', 'pi'],
        ['varrho', 'rho'], ['rho', 'rho'],
        ['varsigma', 'sigma'], ['sigma', 'sigma'],
        ['varphi', 'phi'], ['phi', 'phi'],
        ['alpha', 'alpha'], ['beta', 'beta'], ['gamma', 'gamma'],
        ['delta', 'delta'], ['zeta', 'zeta'], ['eta', 'eta'],
        ['iota', 'iota'], ['kappa', 'kappa'], ['lambda', 'lambda'],
        ['mu', 'mu'], ['nu', 'nu'], ['xi', 'xi'],
        ['tau', 'tau'], ['upsilon', 'upsilon'],
        ['chi', 'chi'], ['psi', 'psi'], ['omega', 'omega'],
        ['Gamma', 'capital gamma'], ['Delta', 'capital delta'],
        ['Theta', 'capital theta'], ['Lambda', 'capital lambda'],
        ['Xi', 'capital xi'], ['Pi', 'capital pi'],
        ['Sigma', 'capital sigma'], ['Phi', 'capital phi'],
        ['Psi', 'capital psi'], ['Omega', 'capital omega'],
    ];
    for (const [k, v] of greek) {
        s = s.replace(new RegExp(`\\\\${k}\\b`, 'g'), ` ${v} `);
    }

    s = s.replace(/\\bullet\b/g, ' ');
    s = s.replace(/\\,/g, ' ');
    s = s.replace(/\\;/g, ' ');
    s = s.replace(/\\!/g, '');
    s = s.replace(/\\\\/g, ', ');

    s = s.replace(
        /(less than or equal to|greater than or equal to|not equal to|approximately|equivalent to|similar to|proportional to|plus or minus|minus or plus|goes to|times|divided by)\s+-(\s*[\d.a-zA-Z(])/g,
        '$1 negative $2'
    );

    const wrap = (x: string) => /[+\-*\/=]/.test(x.trim()) ? `(${x.trim()})` : x.trim();

    const exponentPhrase = (n: string) => {
        const k = parseInt(n, 10);
        if (k === 2) return ' squared ';
        if (k === 3) return ' cubed ';
        const v = k % 100;
        const lastTwoIs11to13 = v >= 11 && v <= 13;
        const lastDigit = k % 10;
        let suffix = 'th';
        if (!lastTwoIs11to13) {
            if (lastDigit === 1) suffix = 'st';
            else if (lastDigit === 2) suffix = 'nd';
            else if (lastDigit === 3) suffix = 'rd';
        }
        return ` to the ${k}${suffix} power `;
    };

    let prev = '';
    let i = 0;
    while (s !== prev && i < 16) {
        prev = s;
        s = s.replace(/\^\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, (_m, a, b) => ` to the ${wrap(a)} over ${wrap(b)} `);
        s = s.replace(/\^\\sqrt\s*\{([^{}]+)\}/g, (_m, a) => ` to the square root of ${wrap(a)} `);
        s = s.replace(/\^\s*\{\s*-\s*(\d+)\s*\}/g, (_m, n) => ` to the negative ${n}${ordinalSuffix(n)} power `);
        s = s.replace(/\^\s*\{\s*(\d+)\s*\}/g, (_m, n) => exponentPhrase(n));
        s = s.replace(/\^\s*-\s*(\d+)/g, (_m, n) => ` to the negative ${n}${ordinalSuffix(n)} power `);
        s = s.replace(/\^\s*(\d+)/g, (_m, n) => exponentPhrase(n));
        s = s.replace(/\^\s*\{([^{}]+)\}/g, (_m, a) => ` to the ${wrap(a)} `);
        s = s.replace(/\^\s*([A-Za-z])/g, ' to the $1 ');
        s = s.replace(/_\s*\{([^{}]+)\}/g, (_m, a) => ` sub ${wrap(a)} `);
        s = s.replace(/_\s*([A-Za-z0-9])/g, ' sub $1 ');
        s = s.replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, (_m, a, b) => ` ${wrap(a)} over ${wrap(b)} `);
        s = s.replace(/\\sqrt\s*\[([^[\]]+)\]\s*\{([^{}]+)\}/g, (_m, n, a) => ` the ${n.trim()} root of ${wrap(a)} `);
        s = s.replace(/\\sqrt\s*\{([^{}]+)\}/g, (_m, a) => ` the square root of ${wrap(a)} `);
        i++;
    }

    s = s.replace(new RegExp(`${PH}T(\\d+)T${PH}`, 'g'), (_m, idx) => ' ' + textChunks[Number(idx)] + ' ');

    s = s.replace(/\\[a-zA-Z]+/g, ' ');
    s = s.replace(/[{}]/g, ' ');
    s = s.replace(/\*/g, ' times ');

    return s.replace(/\s+/g, ' ').trim();
}

function ordinalSuffix(n: string): string {
    const k = parseInt(n, 10);
    const v = k % 100;
    if (v >= 11 && v <= 13) return 'th';
    const last = k % 10;
    if (last === 1) return 'st';
    if (last === 2) return 'nd';
    if (last === 3) return 'rd';
    return 'th';
}
